import styled from "styled-components";

import topographicMap from "static/go-landing-topographic-map.webp";

const raspberry = "#dc1c50";
const spruce = "#01838f";
const deepTeal = "#013c4b";
const warmWhite = "#fbfaf6";
const slate = "#5f6b76";
const mutedSlate = "#8a949c";
const hairline = "#e4e8e2";

export const S = {
  Page: styled.div`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    background-color: #eef0e5;
    background-image: linear-gradient(
        rgba(251, 250, 246, 0.45),
        rgba(251, 250, 246, 0.45)
      ),
      url(${topographicMap});
    background-position: center;
    background-size: cover;
    color: ${slate};

    @supports (min-height: 100dvh) {
      min-height: 100dvh;
    }
  `,

  Header: styled.header`
    height: 66px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    padding: 0 40px;
    background: ${raspberry};

    @media (max-width: 640px) {
      height: 58px;
      padding: 0 20px;
    }
  `,

  HeaderLogoLink: styled.a`
    display: inline-flex;
    align-items: center;
    border-radius: 6px;

    svg {
      display: block;
      width: 152px;
      height: auto;
    }

    &:focus-visible {
      outline: 3px solid #ffffff;
      outline-offset: 5px;
    }

    @media (max-width: 640px) {
      svg {
        width: 128px;
      }
    }
  `,

  Main: styled.main`
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 20px 64px;

    @media (max-width: 640px) {
      padding: 28px 16px 44px;
    }
  `,

  Card: styled.section`
    width: min(100%, 480px);
    box-sizing: border-box;
    padding: 44px 44px 36px;
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 24px 70px rgba(1, 60, 75, 0.22),
      0 4px 16px rgba(1, 60, 75, 0.08);
    text-align: center;

    @media (max-width: 640px) {
      padding: 34px 24px 30px;
      border-radius: 18px;
    }
  `,

  AppIcon: styled.img`
    width: 72px;
    height: 72px;
    border-radius: 17px;
    box-shadow: 0 6px 18px rgba(1, 60, 75, 0.18);

    @media (max-width: 640px) {
      width: 64px;
      height: 64px;
      border-radius: 15px;
    }
  `,

  Eyebrow: styled.p`
    margin: 22px 0 0;
    color: ${raspberry};
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.15em;
    line-height: 1.4;
    text-transform: uppercase;
  `,

  Title: styled.h1`
    margin: 10px 0 0;
    color: ${deepTeal};
    font-size: clamp(28px, 6vw, 36px);
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.12;
  `,

  Description: styled.p`
    max-width: 380px;
    margin: 14px auto 0;
    color: ${slate};
    font-size: 17px;
    line-height: 1.6;

    @media (max-width: 640px) {
      font-size: 16px;
    }
  `,

  OpenAppLink: styled.a`
    width: 100%;
    min-height: 58px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 28px;
    border-radius: 12px;
    background: ${spruce};
    color: #ffffff;
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
    text-decoration: none;
    transition: background-color 160ms ease, transform 160ms ease;

    &:hover {
      background: #006c75;
      color: #ffffff;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    &:focus-visible {
      outline: 3px solid ${raspberry};
      outline-offset: 4px;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `,

  Divider: styled.p`
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 28px 0 18px;
    color: ${mutedSlate};
    font-size: 13px;
    font-weight: 600;
    line-height: 1;

    &::before,
    &::after {
      content: "";
      flex: 1 1 auto;
      height: 1px;
      background: ${hairline};
    }
  `,

  StoreLinks: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
  `,

  StoreLink: styled.a`
    display: block;
    width: 152px;
    height: 45px;
    border-radius: 7px;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }

    &:focus-visible {
      outline: 3px solid ${raspberry};
      outline-offset: 4px;
    }

    @media (max-width: 420px) {
      width: calc(50% - 7px);
      height: auto;
      aspect-ratio: 135 / 40;
    }
  `,

  DownloadHelp: styled.p`
    margin: 16px 0 0;
    color: ${mutedSlate};
    font-size: 13px;
    line-height: 1.5;
  `,

  AndroidPrompt: styled.aside`
    position: fixed;
    z-index: 20;
    right: 18px;
    bottom: 18px;
    left: 18px;
    max-width: 430px;
    min-height: 76px;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 48px 1fr auto auto;
    align-items: center;
    gap: 12px;
    margin-left: auto;
    padding: 12px;
    border: 1px solid ${hairline};
    border-radius: 14px;
    background: ${warmWhite};
    box-shadow: 0 12px 36px rgba(1, 60, 75, 0.2);
  `,

  AndroidPromptIcon: styled.img`
    width: 48px;
    height: 48px;
    border-radius: 11px;
  `,

  AndroidPromptCopy: styled.div`
    min-width: 0;
  `,

  AndroidPromptTitle: styled.strong`
    display: block;
    color: ${deepTeal};
    font-size: 15px;
  `,

  AndroidPromptText: styled.span`
    display: block;
    margin-top: 2px;
    color: ${slate};
    font-size: 12px;
  `,

  AndroidInstallButton: styled.button`
    min-height: 40px;
    padding: 0 14px;
    border: 0;
    border-radius: 9px;
    background: ${spruce};
    color: #ffffff;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 600;

    &:focus-visible {
      outline: 3px solid ${raspberry};
      outline-offset: 3px;
    }
  `,

  AndroidDismissButton: styled.button`
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: ${slate};
    cursor: pointer;

    &:hover {
      background: #eef0eb;
    }

    &:focus-visible {
      outline: 3px solid ${raspberry};
      outline-offset: 2px;
    }
  `,
};
